import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, ClipboardList, RefreshCw } from "lucide-react";
import "../styles/AdminUsersPage.css";
import "../styles/myReportPage.css";
import { API_BASE } from "../config";
import { getSession } from "../session";
import apiFetch from "../apiFetch";

const GAME_LABELS = {
  comprehension:      "Comprehension",
  mcq:                "MCQ Master",
  ranked:             "Ranked",
  book_comprehension: "Book Comprehension",
  vocab_definitions:  "Vocab: Definitions",
  vocab_synonyms:     "Vocab: Synonyms",
  vocab_antonyms:     "Vocab: Antonyms",
  vocab_mixed:        "Vocab: Mixed",
};

function gameLabel(type) {
  return GAME_LABELS[type] || type;
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function fmtDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function SummaryCard({ label, value, color }) {
  return (
    <div className={`rpt-card rpt-card--${color}`}>
      <p className="rpt-card-value">{value ?? "—"}</p>
      <p className="rpt-card-label">{label}</p>
    </div>
  );
}

export default function MyReportPage() {
  const [data,   setData]   = useState(null);
  const [status, setStatus] = useState("loading");

  const session = getSession();

  const fetchReport = async () => {
    setStatus("loading");
    try {
      const res = await apiFetch(`/api/my/report?userId=${session?.userId}`);
      if (!res.ok) { setStatus("error"); return; }
      setData(await res.json());
      setStatus("ok");
    } catch {
      setStatus("error");
    }
  };

  useEffect(() => {
    if (!session) { setStatus("error"); return; }
    fetchReport();
  }, []); // eslint-disable-line

  if (status === "loading") {
    return (
      <div className="myrpt-page">
        <div className="myrpt-loading"><p className="adm-muted">Loading your report…</p></div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="myrpt-page">
        <div className="myrpt-loading">
          <p className="adm-muted">Failed to load your report.</p>
          <button className="adm-btn adm-btn-secondary" onClick={fetchReport}>Retry</button>
        </div>
      </div>
    );
  }

  const { summary, gameBreakdown, recentSessions } = data;
  const hasVocabWords = summary.totalWordsPlayed > 0;

  return (
    <div className="myrpt-page">

      {/* ── Header ── */}
      <div className="myrpt-header">
        <div className="myrpt-header-inner">
          <ClipboardList size={22} className="myrpt-header-icon" />
          <div>
            <h1 className="myrpt-title">My Report</h1>
            <p className="myrpt-subtitle">Your personal activity across all games and tests</p>
          </div>
        </div>
        <div className="myrpt-header-actions">
          <Link to="/statisticsPage" className="myrpt-action-btn">
            <TrendingUp size={15} />
            Statistics
          </Link>
          <button className="myrpt-action-btn" onClick={fetchReport} title="Refresh">
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      <div className="myrpt-body">

        {/* ── Summary cards ── */}
        <div className="rpt-summary">
          <SummaryCard label="Total plays" value={summary.totalPlays} color="blue"   />
          <SummaryCard label="Game plays"  value={summary.gamePlays}  color="purple" />
          <SummaryCard label="Vocab tests" value={summary.vocabPlays} color="green"  />
          <SummaryCard label="Last active" value={fmtDate(summary.lastActive)} color="yellow" />
        </div>

        {/* ── Vocab word stats cards ── */}
        {hasVocabWords && (
          <div className="rpt-summary">
            <SummaryCard label="Words played"  value={summary.totalWordsPlayed.toLocaleString()}    color="teal"   />
            <SummaryCard label="Words correct"  value={summary.totalWordsCorrect.toLocaleString()}  color="green"  />
            <SummaryCard label="Words wrong"    value={summary.totalWordsIncorrect.toLocaleString()} color="red"   />
          </div>
        )}

        {/* ── Game breakdown ── */}
        <section className="rpt-section">
          <h2 className="rpt-section-title">Activity by game</h2>
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Game / Test</th>
                  <th>Plays</th>
                  <th>Avg score</th>
                  <th>Words played</th>
                  <th>Correct</th>
                  <th>Wrong</th>
                  <th>Last played</th>
                </tr>
              </thead>
              <tbody>
                {gameBreakdown.length === 0 && (
                  <tr><td colSpan={7} className="adm-empty">No game data yet.</td></tr>
                )}
                {gameBreakdown.map((g) => (
                  <tr key={g.type}>
                    <td><strong>{gameLabel(g.type)}</strong></td>
                    <td>{Number(g.plays).toLocaleString()}</td>
                    <td className="adm-muted-cell">
                      {g.avg_pct != null ? `${g.avg_pct}%` : "—"}
                    </td>
                    <td className="adm-muted-cell">
                      {g.words_played != null ? Number(g.words_played).toLocaleString() : "—"}
                    </td>
                    <td className="adm-muted-cell rpt-correct-cell">
                      {g.words_correct != null ? Number(g.words_correct).toLocaleString() : "—"}
                    </td>
                    <td className="adm-muted-cell rpt-wrong-cell">
                      {g.words_incorrect != null ? Number(g.words_incorrect).toLocaleString() : "—"}
                    </td>
                    <td className="adm-muted-cell">{fmtDateTime(g.last_played)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── Recent sessions ── */}
        <section className="rpt-section">
          <h2 className="rpt-section-title">Recent sessions</h2>
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Game / Test</th>
                  <th>Title</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                {recentSessions.length === 0 && (
                  <tr><td colSpan={4} className="adm-empty">No sessions yet.</td></tr>
                )}
                {recentSessions.map((s) => {
                  const pct = s.max_score
                    ? Math.round((s.score / s.max_score) * 100)
                    : null;
                  return (
                    <tr key={`${s.source}-${s.id}`}>
                      <td className="adm-muted-cell">{fmtDateTime(s.played_at)}</td>
                      <td><strong>{gameLabel(s.type)}</strong></td>
                      <td className="adm-muted-cell">{s.title || "—"}</td>
                      <td>
                        {s.score != null
                          ? <>{s.score}/{s.max_score}{pct != null && <span className="adm-muted-cell"> ({pct}%)</span>}</>
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

      </div>
    </div>
  );
}
